import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';

/**
 * VoiceAudioTestTab — bot-vs-bot VOICE testing.
 * Emily (Nova Lite text) → Polly TTS → LPCM 16kHz → Nova Sonic receptionist.
 * Nova's audio output archived; playable per scenario after run completes.
 *
 * Differences from BatchTestTab (text mode):
 *  - Sends { commMode: 'audio' } on the run POST
 *  - Forces parallelism=1 (audio streaming can't parallelize meaningfully)
 *  - Shows S3 audio download links per scenario after the run finishes
 *  - Warns on cost (~5x text mode)
 */
function VoiceAudioTestTab({ practiceId }) {
  const [presets, setPresets] = useState({});
  const [scenarios, setScenarios] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('');
  const [selectedScenarios, setSelectedScenarios] = useState([]);
  const [customPick, setCustomPick] = useState(false);
  const [running, setRunning] = useState(false);
  const [activeRun, setActiveRun] = useState(null);
  const [runScenarios, setRunScenarios] = useState([]);
  const [error, setError] = useState('');
  const [audioUrls, setAudioUrls] = useState({});    // { scenarioKey: {emily, nova, transcript} }
  const pollRef = useRef(null);

  // Load scenarios + presets
  useEffect(() => {
    if (!practiceId) return;
    api.request(`/practices/${practiceId}/voice-test/scenarios`)
      .then(res => {
        setScenarios(res?.scenarios || []);
        setPresets(res?.presets || {});
        // Default to 'p0' preset if it exists
        if (res?.presets?.p0) setSelectedPreset('p0');
      })
      .catch(err => setError('load scenarios failed: ' + err.message));
  }, [practiceId]);

  // Poll active run
  useEffect(() => {
    if (!activeRun?.id) return;
    const tick = () => {
      api.request(`/practices/${practiceId}/voice-test/runs/${activeRun.id}`)
        .then(res => {
          setActiveRun(res.run);
          setRunScenarios(res.scenarios || []);
          if (res.run.status && res.run.status !== 'running' && res.run.status !== 'pending') {
            clearInterval(pollRef.current);
            setRunning(false);
          }
        })
        .catch(() => {});
    };
    tick();
    pollRef.current = setInterval(tick, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeRun?.id, practiceId]);

  const effectiveScenarios = customPick
    ? selectedScenarios
    : (presets[selectedPreset] || []);
  const scenarioCount = effectiveScenarios.length;
  const costEstUsd = (scenarioCount * 0.80).toFixed(2);   // ~$0.80/scenario for mock+audio

  const startRun = async () => {
    setError('');
    if (!scenarioCount) { setError('Pick a preset or at least one scenario.'); return; }
    if (scenarioCount > 20 && !window.confirm(`This will run ${scenarioCount} audio scenarios at ~$${costEstUsd}. Continue?`)) return;
    try {
      setRunning(true);
      const body = {
        mode: 'mock',
        commMode: 'audio',
        parallelism: 1,
        ...(customPick ? { scenarios: selectedScenarios } : { preset: selectedPreset }),
      };
      const res = await api.request(`/practices/${practiceId}/voice-test/runs`, { method: 'POST', body });
      setActiveRun(res.run);
      setRunScenarios([]);
      setAudioUrls({});
    } catch (err) {
      setError('start run failed: ' + err.message);
      setRunning(false);
    }
  };

  const cancelRun = async () => {
    if (!activeRun?.id) return;
    if (!window.confirm('Cancel this run?')) return;
    try {
      await api.request(`/practices/${practiceId}/voice-test/runs/${activeRun.id}/cancel`, { method: 'POST' });
    } catch (err) {
      setError('cancel failed: ' + err.message);
    }
  };

  const loadAudioUrls = async (scenarioKey) => {
    if (!activeRun?.id) return;
    try {
      const res = await api.request(`/practices/${practiceId}/voice-test/runs/${activeRun.id}/scenarios/${scenarioKey}/audio-urls`);
      setAudioUrls(prev => ({ ...prev, [scenarioKey]: res }));
    } catch (err) {
      setError(`load audio for ${scenarioKey}: ` + err.message);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.h3}>Voice Test (audio bot-vs-bot)</h3>
        <p style={styles.tagline}>
          Emily (Nova Lite) → Polly TTS → Nova Sonic receptionist. Real audio path. Archived to S3.
          ~5× the cost of text mode; forced parallelism=1.
        </p>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.controls}>
        <label style={styles.label}>
          <input type="checkbox" checked={customPick} onChange={e => setCustomPick(e.target.checked)} />
          &nbsp;pick individual scenarios
        </label>

        {!customPick ? (
          <div>
            <label style={styles.label}>preset:&nbsp;</label>
            <select value={selectedPreset} onChange={e => setSelectedPreset(e.target.value)} style={styles.select}>
              {Object.keys(presets).map(p => (
                <option key={p} value={p}>{p} ({(presets[p] || []).length} scenarios)</option>
              ))}
            </select>
          </div>
        ) : (
          <div style={styles.scenarioList}>
            {scenarios.map(s => (
              <label key={s.key} style={styles.scenarioItem}>
                <input
                  type="checkbox"
                  checked={selectedScenarios.includes(s.key)}
                  onChange={e => {
                    setSelectedScenarios(prev =>
                      e.target.checked ? [...prev, s.key] : prev.filter(k => k !== s.key)
                    );
                  }}
                />
                &nbsp;{s.name}
              </label>
            ))}
          </div>
        )}

        <div style={styles.runRow}>
          <button onClick={startRun} disabled={running || !scenarioCount} style={styles.runBtn}>
            {running ? 'Running...' : `Run ${scenarioCount} audio scenario${scenarioCount === 1 ? '' : 's'}`}
          </button>
          {running && (
            <button onClick={cancelRun} style={styles.cancelBtn}>Cancel</button>
          )}
          <span style={styles.cost}>est. ~${costEstUsd}</span>
        </div>
      </div>

      {activeRun && (
        <div style={styles.runPanel}>
          <div style={styles.runHeader}>
            <strong>Run #{activeRun.id}</strong> ({activeRun.comm_mode || 'text'} / {activeRun.mode})
            <span style={styles.runStatus}>{activeRun.status}</span>
            <span style={styles.runCounts}>
              PASS {activeRun.pass_count || 0} · PARTIAL {activeRun.partial_count || 0} · FAIL {activeRun.fail_count || 0} /
              {activeRun.total_scenarios || '?'}
            </span>
          </div>

          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Scenario</th>
                <th style={styles.th}>Verdict</th>
                <th style={styles.th}>Score</th>
                <th style={styles.th}>Turns</th>
                <th style={styles.th}>Dur</th>
                <th style={styles.th}>Audio</th>
              </tr>
            </thead>
            <tbody>
              {runScenarios.map(s => (
                <tr key={s.scenario_key} style={styles.tr}>
                  <td style={styles.td}>{s.scenario_key}</td>
                  <td style={{ ...styles.td, color: verdictColor(s.verdict) }}>{s.verdict || '-'}</td>
                  <td style={styles.td}>{s.score}</td>
                  <td style={styles.td}>{s.turn_count || 0}</td>
                  <td style={styles.td}>{s.duration_ms ? `${(s.duration_ms/1000).toFixed(1)}s` : '-'}</td>
                  <td style={styles.td}>
                    {audioUrls[s.scenario_key] ? (
                      <div>
                        {audioUrls[s.scenario_key].conversation ? (
                          <div>
                            <div style={styles.audioLabel}>full conversation</div>
                            <audio controls src={audioUrls[s.scenario_key].conversation} style={styles.audioBig}>Conversation</audio>
                          </div>
                        ) : null}
                        <details style={styles.details}>
                          <summary style={styles.summary}>individual streams</summary>
                          {audioUrls[s.scenario_key].emily && (
                            <div>
                              <span style={styles.audioLabel}>Emily (patient):</span>
                              <audio controls src={audioUrls[s.scenario_key].emily} style={styles.audio}>Emily</audio>
                            </div>
                          )}
                          {audioUrls[s.scenario_key].nova && (
                            <div>
                              <span style={styles.audioLabel}>Nova (receptionist):</span>
                              <audio controls src={audioUrls[s.scenario_key].nova} style={styles.audio}>Nova</audio>
                            </div>
                          )}
                        </details>
                      </div>
                    ) : (
                      s.verdict && <button onClick={() => loadAudioUrls(s.scenario_key)} style={styles.loadBtn}>Load audio</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function verdictColor(v) {
  if (v === 'PASS') return '#0a7d3e';
  if (v === 'FAIL') return '#c62828';
  if (v === 'PARTIAL') return '#c67f00';
  return '#666';
}

const styles = {
  container: { padding: 16, maxWidth: 1200 },
  header: { marginBottom: 16 },
  h3: { margin: '0 0 4px 0', fontSize: 18 },
  tagline: { color: '#666', fontSize: 13, margin: 0 },
  error: { padding: 10, background: '#fee', border: '1px solid #fbb', borderRadius: 4, marginBottom: 12, color: '#900' },
  controls: { display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16, padding: 12, background: '#f7f7f7', borderRadius: 6 },
  label: { fontSize: 13 },
  select: { padding: '4px 8px', fontSize: 13 },
  scenarioList: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, maxHeight: 200, overflowY: 'auto', padding: 8, background: '#fff', border: '1px solid #ddd', borderRadius: 4 },
  scenarioItem: { fontSize: 12 },
  runRow: { display: 'flex', gap: 12, alignItems: 'center' },
  runBtn: { padding: '8px 16px', fontSize: 14, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' },
  cancelBtn: { padding: '8px 12px', fontSize: 14, background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' },
  cost: { color: '#666', fontSize: 13 },
  runPanel: { border: '1px solid #ddd', borderRadius: 6, padding: 12 },
  runHeader: { marginBottom: 12, display: 'flex', gap: 16, alignItems: 'center', fontSize: 14 },
  runStatus: { padding: '2px 8px', background: '#eef', borderRadius: 4, fontSize: 12 },
  runCounts: { color: '#555', fontSize: 12 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: 6, borderBottom: '2px solid #ccc', fontSize: 12, background: '#fafafa' },
  tr: { borderBottom: '1px solid #eee' },
  td: { padding: 6, fontSize: 12, verticalAlign: 'top' },
  audio: { height: 24, verticalAlign: 'middle', marginRight: 4 },
  audioBig: { height: 32, width: 280 },
  audioLabel: { fontSize: 11, color: '#555', marginRight: 6 },
  details: { marginTop: 6 },
  summary: { fontSize: 11, color: '#555', cursor: 'pointer', userSelect: 'none' },
  loadBtn: { padding: '2px 8px', fontSize: 11, cursor: 'pointer' },
};

export default VoiceAudioTestTab;
